import { CctvStatus, Cctv, CctvUpdate, CctvCreate } from '../types/cctv';
import { STATUS_MAPPING, STATUS_REVERSE_MAPPING } from '../constants/cctv';
import { formatDateForBackend } from './dateUtils';

export function parseRiskZonesJson(raw: string | undefined | null): number[][] | undefined {
  if (raw == null || String(raw).trim() === '') return undefined;
  try {
    const v = JSON.parse(String(raw).trim());
    if (!Array.isArray(v)) return undefined;
    return v as number[][];
  } catch {
    return undefined;
  }
}

function numOrUndef(v: unknown): number | undefined {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : undefined;
}

/** 빈 문자열 → null (시설 폴백), 숫자면 그대로 */
function numOrNull(v: unknown): number | null | undefined {
  if (v === '' || v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

function intOrNull(v: unknown): number | null | undefined {
  if (v === '' || v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : parseInt(String(v), 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * 한글 상태값을 영문으로 변환
 */
export const convertStatusToEnglish = (status: string): CctvStatus => {
  return (STATUS_MAPPING[status as keyof typeof STATUS_MAPPING] || 'ACTIVE') as CctvStatus;
};

/**
 * 영문 상태값을 한글로 변환
 */
export const convertStatusToKorean = (status: string): string => {
  return STATUS_REVERSE_MAPPING[status as keyof typeof STATUS_REVERSE_MAPPING] || '정상';
};

/**
 * 용도값을 한글로 변환
 */
export const convertPurposeToKorean = (purpose: string): string => {
  const purposeMapping = {
    'safety': '안전 모니터링',
    'management': '관리 모니터링',
    'other': '기타 모니터링'
  };
  return purposeMapping[purpose as keyof typeof purposeMapping] || '기타 모니터링';
};

/**
 * 용도값을 영문으로 변환
 */
export const convertPurposeToEnglish = (purpose: string): string => {
  const purposeReverseMapping = {
    '안전 모니터링': 'safety',
    '관리 모니터링': 'management',
    '기타 모니터링': 'other'
  };
  return purposeReverseMapping[purpose as keyof typeof purposeReverseMapping] || 'other';
};

/**
 * CCTV 데이터를 폼 데이터 형식으로 변환
 */
export const convertCctvToFormData = (cctv: Cctv) => {
  // 상태값이 영문이면 그대로 유지
  const status = cctv.status || 'ACTIVE';
  
  return {
    id: cctv.id,
    name: cctv.name || '',
    pool_id: cctv.pool_id,
    location: cctv.location || '',
    ip_address: cctv.ip_address || '',
    port: cctv.port || 0,
    username: cctv.username || null,
    password: cctv.password || null,
    status: status,
    is_active: cctv.is_active === null ? false : cctv.is_active,
    purpose: cctv.purpose || '관제 모니터링',
    alert_type: cctv.alert_type || '',
    alert_severity: cctv.alert_severity || '',
    alert_description: cctv.alert_description || '',
    code: cctv.code || '',
    stream_url: cctv.stream_url || '',
    playback_url: cctv.playback_url || '',
    model: cctv.model || '',
    manufacturer: cctv.manufacturer || '',
    description: cctv.description || '',
    last_maintenance: cctv.last_maintenance || null,
    next_maintenance: cctv.next_maintenance || null,
    created_at: cctv.created_at || null,
    updated_at: cctv.updated_at || null,
    risk_use_pool_motion_defaults: cctv.risk_use_pool_motion_defaults !== false,
    risk_detection_sensitivity:
      cctv.risk_detection_sensitivity != null ? cctv.risk_detection_sensitivity : 0.75,
    risk_detection_stillness_sec:
      cctv.risk_detection_stillness_sec != null ? cctv.risk_detection_stillness_sec : 10,
    risk_detection_zones_json:
      cctv.risk_detection_zones != null && cctv.risk_detection_zones.length > 0
        ? JSON.stringify(cctv.risk_detection_zones)
        : '[]',
    special_management_zones_json:
      cctv.special_management_zones != null && cctv.special_management_zones.length > 0
        ? JSON.stringify(cctv.special_management_zones)
        : '[]',
    special_zone_dwell_sec:
      cctv.special_zone_dwell_sec != null && cctv.special_zone_dwell_sec !== undefined
        ? cctv.special_zone_dwell_sec
        : '',
    ai_detection_conf:
      cctv.ai_detection_conf != null && cctv.ai_detection_conf !== undefined
        ? cctv.ai_detection_conf
        : '',
    ai_detection_min_consecutive_hits:
      cctv.ai_detection_min_consecutive_hits != null &&
      cctv.ai_detection_min_consecutive_hits !== undefined
        ? cctv.ai_detection_min_consecutive_hits
        : '',
    monitor_filter_strength:
      cctv.monitor_filter_strength != null && cctv.monitor_filter_strength !== undefined
        ? cctv.monitor_filter_strength
        : '',
    tracker_type: cctv.tracker_type || null,
    mount_type: cctv.mount_type === 'oblique' ? 'oblique' : 'overhead',
    rotate_180: Boolean(cctv.rotate_180),
    detection_scenario: cctv.detection_scenario || 'pool_drowning',
    detection_purpose: cctv.detection_purpose || '',
    ai_detector_model: cctv.ai_detector_model || '',
  };
};

/**
 * 폼 데이터를 CCTV 업데이트 데이터로 변환
 */
export const convertFormDataToUpdateData = (formData: any): CctvUpdate => {
  return {
    id: formData.id || 0,
    pool_id: formData.pool_id || 0,
    name: formData.name,
    status: convertStatusToEnglish(formData.status),
    location: formData.location,
    ip_address: formData.ip_address,
    port: formData.port,
    username: formData.username,
    password: formData.password,
    is_active: formData.is_active,
    purpose: formData.purpose,
    alert_type: formData.alert_type,
    alert_severity: formData.alert_severity,
    alert_description: formData.alert_description,
    alert_created_at: formatDateForBackend(formData.alert_created_at),
    code: formData.code,
    stream_url: formData.stream_url,
    model: formData.model || '',
    manufacturer: formData.manufacturer || '',
    description: formData.description || '',
    last_maintenance_date: formatDateForBackend(formData.last_maintenance),
    next_maintenance_date: formatDateForBackend(formData.next_maintenance),
    created_at: formatDateForBackend(formData.created_at),
    updated_at: formatDateForBackend(formData.updated_at),
    risk_use_pool_motion_defaults: formData.risk_use_pool_motion_defaults !== false,
    risk_detection_sensitivity: numOrUndef(formData.risk_detection_sensitivity),
    risk_detection_stillness_sec: numOrUndef(formData.risk_detection_stillness_sec),
    risk_detection_waterline_y: null,
    risk_detection_zones: parseRiskZonesJson(formData.risk_detection_zones_json),
    special_management_zones: parseRiskZonesJson(
      formData.special_management_zones_json
    ),
    special_zone_dwell_sec: numOrNull(formData.special_zone_dwell_sec),
    ai_detection_conf: numOrNull(formData.ai_detection_conf),
    ai_detection_min_consecutive_hits: intOrNull(formData.ai_detection_min_consecutive_hits),
    monitor_filter_strength: intOrNull(formData.monitor_filter_strength),
    tracker_type: String(formData.tracker_type || '').trim() || null,
    mount_type: formData.mount_type === 'oblique' ? 'oblique' : 'overhead',
    rotate_180: Boolean(formData.rotate_180),
    detection_scenario: formData.detection_scenario || 'pool_drowning',
    detection_purpose: formData.detection_purpose || null,
    ai_detector_model: String(formData.ai_detector_model || '').trim() || null,
  };
};

/**
 * 폼 데이터를 CCTV 생성 데이터로 변환
 */
export const convertFormDataToCreateData = (formData: any, poolId: number): CctvCreate => {
  const updateLike = convertFormDataToUpdateData(formData);
  return {
    name: updateLike.name || '',
    location: updateLike.location || '',
    stream_url: updateLike.stream_url || '',
    status: updateLike.status || 'INACTIVE',
    code: updateLike.code || `CCTV-${poolId}-${Date.now().toString().slice(-6)}`,
    is_active: typeof updateLike.is_active === 'boolean' ? updateLike.is_active : true,
    pool_id: poolId,
    ...updateLike
  };
}; 